import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import { darkModeBlack } from "../../colors";
import Button, { ButtonStyleType } from "../atoms/Button";
import "../components.css";

interface Props {
  open: boolean;
  darkModeActive: boolean;
  onBackdropClick: () => void;
  onCancel: () => void;
  onClose: () => void;
  onDateUpdate: (startDate: string, endDate: string) => void;
  selectedStartDate: string;
  selectedEndDate: string;
}

const months = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const WorkplaceDatesDialog = (props: Props) => {
  const [selectedStartYearValue, setSelectedStartYearValue] =
    useState<string>("");
  const [selectedEndYearValue, setSelectedEndYearValue] = useState<string>("");
  const [selectedStartMonthValue, setSelectedStartMonthValue] =
    useState<string>("01");
  const [selectedEndMonthValue, setSelectedEndMonthValue] =
    useState<string>("01");

  useEffect(() => {
    if (props.selectedStartDate !== "") {
      if (props.selectedStartDate.length === 4) {
        // Legacy YYYY format, default to January
        setSelectedStartYearValue(props.selectedStartDate);
        setSelectedStartMonthValue("01");
      } else if (props.selectedStartDate.includes("-")) {
        // YYYY-MM format
        const [year, month] = props.selectedStartDate.split("-");
        setSelectedStartYearValue(year);
        setSelectedStartMonthValue(month);
      }
    }

    if (props.selectedEndDate !== "") {
      if (props.selectedEndDate.length === 4) {
        // Legacy YYYY format, default to January
        setSelectedEndYearValue(props.selectedEndDate);
        setSelectedEndMonthValue("01");
      } else if (props.selectedEndDate.includes("-")) {
        // YYYY-MM format
        const [year, month] = props.selectedEndDate.split("-");
        setSelectedEndYearValue(year);
        setSelectedEndMonthValue(month);
      }
    }
  }, []);

  return (
    <div
      className="bg-op-dark absolute top-0 left-0 w-full h-full bg-opacity-50"
      onClick={() => props.onBackdropClick()}
    >
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-8 rounded-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h6 className="text-xl font-bold">Workplace dates</h6>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            When did you start here?
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <select
                value={selectedStartYearValue}
                onChange={(e) => {
                  setSelectedStartYearValue(e.target.value);
                  const startDate = e.target.value
                    ? `${e.target.value}-${selectedStartMonthValue}`
                    : "";
                  const endDate = selectedEndYearValue
                    ? `${selectedEndYearValue}-${selectedEndMonthValue}`
                    : "";
                  props.onDateUpdate(startDate, endDate);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Start Year</option>
                {Array.from(
                  { length: new Date().getFullYear() - 1950 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            {selectedStartYearValue && (
              <div className="flex-1">
                <select
                  value={selectedStartMonthValue}
                  onChange={(e) => {
                    setSelectedStartMonthValue(e.target.value);
                    const startDate = selectedStartYearValue
                      ? `${selectedStartYearValue}-${e.target.value}`
                      : "";
                    const endDate = selectedEndYearValue
                      ? `${selectedEndYearValue}-${selectedEndMonthValue}`
                      : "";
                    props.onDateUpdate(startDate, endDate);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            When (if) did you leave this position? Leave empty if this is your
            current position
          </label>
          <div className="flex gap-4">
            <div className="w-1/2">
              <select
                disabled={!selectedStartYearValue}
                value={selectedEndYearValue}
                onChange={(e) => {
                  setSelectedEndYearValue(e.target.value);
                  const startDate = selectedStartYearValue
                    ? `${selectedStartYearValue}-${selectedStartMonthValue}`
                    : "";
                  const endDate = e.target.value
                    ? `${e.target.value}-${selectedEndMonthValue}`
                    : "";
                  props.onDateUpdate(startDate, endDate);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">End Year</option>
                {Array.from(
                  { length: new Date().getFullYear() - 1950 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map((year) => (
                  <option key={year} value={year.toString()}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            {selectedEndYearValue && (
              <div className="w-1/2">
                <select
                  value={selectedEndMonthValue}
                  onChange={(e) => {
                    setSelectedEndMonthValue(e.target.value);
                    const startDate = selectedStartYearValue
                      ? `${selectedStartYearValue}-${selectedStartMonthValue}`
                      : "";
                    const endDate = selectedEndYearValue
                      ? `${selectedEndYearValue}-${e.target.value}`
                      : "";
                    props.onDateUpdate(startDate, endDate);
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flex: 1,
            justifyContent: "flex-end",
            marginTop: 20,
          }}
        >
          <Button
            text="Close"
            onButtonClick={props.onClose}
            style={ButtonStyleType.primary}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkplaceDatesDialog;
